import { createModel } from '@rematch/core';
import _ from 'lodash';

import { RootModel } from '../index';
import { apiUrls, replaceUrls } from '../../api/apiUrls';
import { get, post } from '../../api/api';
import LottoState, { SelectedNumber, LottoPermutations, FastInputHistory } from '../../interfaces/LottoState';

export default createModel<RootModel>()({
  state: {
    numberEvents: [],
    popularEvents: [],
    singleNumberEvent: {
      country:'',
      name:'',
      countryCode:'',
      events: [],
      marketType: [],
      markets: [],
      tournamentMarkets: [],
      numberInfos:{
        description: '',
        bonus: { sameBallset: true }
      }
    },
    fastModeRules: {},
    lottoErrors: { betcode:'', race:'', betType:'', numbers:'', stake:'' },
    lottoValues: { betCode:'', race:'CR', betType:'', numbers:'', stake:'' },
    usedNumbers: [''],
    fastInputHistory: [],
    valuesReset: false
  } as LottoState,

  reducers:{
    setNumber(state, payload: LottoState['singleNumberEvent']) {
      return {
        ...state,
        singleNumberEvent: {
          ...payload,
          marketType: _.isEqual(state.singleNumberEvent.marketType, payload.marketType) ? state.singleNumberEvent.marketType : payload.marketType }
      };
    },

    setErrors(state, payload: LottoState['lottoErrors']){
      return { ...state, lottoErrors:payload };
    },

    setValues(state, payload: LottoState['lottoValues']){
      return { ...state, lottoValues:payload };
    },

    setFastModeRules(state, payload: LottoState['fastModeRules']){
      return { ...state, fastModeRules: payload };
    },

    setData(state, payload: {}) {
      return { ...state, ...payload };
    },

    changeMarketDefault(state, payload: {market: number, newDefault: number}){
      const { marketType } = state.singleNumberEvent;
      const { market, newDefault } = payload;
      const newMarketType = marketType;
      newMarketType[market].default = newDefault;

      return {
        ...state,
        singleNumberEvent: {
          ...state.singleNumberEvent,
          marketType: newMarketType
        }
      };
    },

    resetRetailValues(state){
      return {
        ...state,
        lottoErrors: { betcode:'', race:'', betType:'', numbers:'', stake:'' },
        lottoValues: { betCode:'', race:'CR', betType:'', numbers:'', stake:'' },
        valuesReset: true
      };
    },

    resetRetailErrors(state){
      return {
        ...state,
        lottoErrors: { betcode:'', race:'', betType:'', numbers:'', stake:'' }
      };
    },

    setUsedNumbers(state, payload: LottoState['usedNumbers']){
      return {
        ...state,
        usedNumbers: payload
      };
    },

    setNumberHistory(state, payload: FastInputHistory){
      return {
        ...state,
        fastInputHistory: [payload, ...state.fastInputHistory]
      };
    },

    resetHistory(state){
      return {
        ...state,
        fastInputHistory: []
      };
    },

    setResetActive(state){
      return {
        ...state,
        valuesReset: false
      };
    }
  },

  effects: (dispatch) => ({
    async getNumbersEvents(payload: { hideLoading?: boolean, module: number, hours: number }): Promise<void> {
      try {
        const { hideLoading, module, hours } = payload;

        if (!hideLoading) dispatch.events.setLoading(true);

        const response = await get(apiUrls.numbersBetting + `?type=${ module }&range=${ hours }`);
        if (response && response.status === 200) {
          await dispatch.lotto.setData({
            nextEvents: response.data.results.nextEvents,
            popularEvents: response.data.results.popularEvents
          });
        }

        if (!hideLoading) dispatch.events.setLoading(false);

      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    activateResetNumbers(){
      dispatch.lotto.setResetActive();
    },

    async getNumberEventByCode(payload, state): Promise<void> {
      try {
        const { hideLoading, tournamentCode, eventId } = payload;

        if (!hideLoading) dispatch.events.setLoading(true);
        const tillId = localStorage.getItem('tillId') ? localStorage.getItem('tillId') : -1;
        const customerId = state.auth.customerId ? state.auth.customerId : -1;

        const response = await get(
          replaceUrls(apiUrls.numberBet + `?tillId=${ tillId }&customerId=${ customerId }`, { tournamentCode, eventId })
        );
        if (response && response.status === 200) {
          const { results } = response.data;

          if (!_.some(results.events, (evt) => evt.selected === true)){
            results.events[0].selected = true;
          }

          dispatch.lotto.setNumber(results);
          if (!hideLoading) dispatch.events.setLoading(false);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    async permutations(payload:LottoPermutations): Promise<any> {
      try {
        const response = await post(apiUrls.numberCombinations, payload);
        if (response && response.status === 200){
          return response.data.results;
        }
      } catch (error) {
        return error;
      }
    },

    async getRandomNumber(payload): Promise<SelectedNumber[] | { regularDrum: SelectedNumber[], bonusDrum: SelectedNumber[]}> {
      try {
        if (!_.isArray(payload)) {
          const { from, to, balls, type } = payload;
          dispatch.events.setLoading(true);

          const response = await get(replaceUrls(apiUrls.randomNumber, { from, to, balls, type }));

          if (response && response.status === 200) {
            const { result } = response.data.results.random;
            const number: SelectedNumber[] = _.map(result, (number) => {
              return { number, type, isBanker: false };
            });

            return number;
          } else {
            return null;
          }
        } else {
          const isSameDrum = payload[1].sameDrum;
          let bonusResponse;
          const regularResponse = await get(replaceUrls(apiUrls.randomNumber, {
            from: payload[0].from,
            to: payload[0].to,
            balls: isSameDrum ? payload[0].balls + payload[1].balls : payload[0].balls
          }));
          if (!isSameDrum){
            bonusResponse = await get(replaceUrls(apiUrls.randomNumber, { from: payload[1].from, to: payload[1].to, balls: payload[1].balls }));
          }

          const response = { regularDrum: [], bonusDrum: [] };
          if (regularResponse && regularResponse.status === 200) {
            const drum = regularResponse.data.results?.random?.result;
            if (drum) {
              if (!isSameDrum){
                response.regularDrum = _.map(drum, (number) => {
                  return { number, type: payload[0].type, isBanker: false };
                });
              } else {
                response.regularDrum = _.map(drum, (number, index) => {
                  return { number, type: index + 1 <= payload[0].balls ? payload[0].type : payload[1].type, isBanker: false };
                });
              }
            }
          }

          if (bonusResponse && bonusResponse.status === 200) {
            const drum = bonusResponse.data.results?.random?.result;
            if (drum) {
              response.bonusDrum = _.map(drum, (number) => {
                return { number, type: payload[1].type, isBanker: false };
              });
            }
          }

          return response;
        }
      } catch (error) {
        return null;
      }
    },

    async getPreviousDraws(payload): Promise<any> {
      try {
        const { tournament, page } = payload;
        const response = await get(replaceUrls(apiUrls.numResults + `?page=${ page }`, { tournament }));
        dispatch.events.setLoading(true);

        if (response && response.status === 200) {
          return response.data.results;
        }
      } catch (error) {
        return null;
      }
    },

    async changeDefaultMarket(payload): Promise<any> {
      dispatch.lotto.changeMarketDefault(payload);
    },

    async changeErrors (payload, state) {
      const { value, error } = payload;

      if (value === ''){
        dispatch.lotto.setErrors({ betcode:'', race:'', betType:'', numbers:'', stake:'' });
      } else {
        const errors = { ...state.lottoErrors, [value]: error };
        dispatch.lotto.setErrors(errors);
      }
    },

    async getNumbersColours(payload){
      try {
        const { tournamentCode } = payload;
        dispatch.events.setLoading(true);

        const response = await get(replaceUrls(apiUrls.numberColours, { tournamentCode }));

        if (response && response.status === 200) {
          dispatch.lotto.setFastModeRules(response.data.results);
        }
        dispatch.events.setLoading(false);

      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    changeUsedNumbers(payload){
      dispatch.lotto.setUsedNumbers(payload);
    },

    async changeValues (payload, state) {
      const { key, value } = payload;
      const values = { ...state.lotto.lottoValues, [key]: value };
      dispatch.lotto.setValues(values);
    },

    addNumberToFastInputHistory(payload){
      dispatch.lotto.setNumberHistory(payload);
    },

    resetLottoRetailValues(){
      dispatch.lotto.resetRetailValues();
    },

    resetFastModeErrors(){
      dispatch.lotto.resetRetailErrors();
    },

    resetLottoFastModeHistory(){
      dispatch.lotto.resetHistory();
    }
  })
});
